import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Moon, Sun, ArrowLeft, ChevronDown, Check, Flame } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageCode, AppMode } from '../types/types';
import './MobileHeader.css';
import { useTheme } from './ThemeContext';
import GoogleAuth from './GoogleAuth';
import subscriptionService, { SubscriptionTier } from '../services/subscriptionService';
import { getLevel } from '../data/levelTitles';

import { EmojiText } from './common/Picto';
// ===============================================
// 🏛️ VARIANTS ANIMATIONS ÉGYPTIENNES
// ===============================================
const buttonVariants = {
  initial: {},
  hover: { 
    scale: 1.05, 
    y: -2,
    transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] }
  },
  tap: { 
    scale: 0.95,
    y: 0,
    transition: { duration: 0.15 }
  }
};

// ===============================================
// INTERFACE
// ===============================================
interface MobileHeaderProps {
  title: string;
  showBackButton?: boolean;
  onBack?: () => void;
  languageCode?: LanguageCode;
  onLanguageChange?: (code: LanguageCode) => void;
  showLanguageSelector?: boolean;
  currentMode: AppMode;
  hasSelectedCategory?: boolean;
  onShowExitConfirmation?: () => void;
  onLogin: (response: any) => void;
  onLogout: () => void;
  onOpenSubscription?: () => void;
  subscriptionTier?: SubscriptionTier;
  onStreakClick?: () => void;
  user?: { name?: string; picture?: string; email?: string } | null;
}

// ===============================================
// COMPOSANT PRINCIPAL
// ===============================================
const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  showBackButton,
  onBack,
  languageCode,
  onLanguageChange,
  showLanguageSelector = false,
  currentMode,
  hasSelectedCategory,
  onShowExitConfirmation,
  onLogin,
  onLogout,
  onOpenSubscription,
  subscriptionTier = SubscriptionTier.FREE,
  onStreakClick,
  user,
}) => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const isDarkMode = theme === 'dark';
  const headerRef = useRef<HTMLElement>(null);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [xp, setXp] = useState(0);
  
  const isHeaderVisible = true;
  
  // État pour la surveillance de l'abonnement
  const [currentSubscriptionTier, setCurrentSubscriptionTier] = useState<SubscriptionTier>(subscriptionTier);

  const isMainMenu = true;
  
  // Détermine quoi afficher selon le contexte
  const shouldShowLanguageSelector = isMainMenu && showLanguageSelector && languageCode && onLanguageChange;
  const shouldShowTitle = !isMainMenu && title;


  // ===============================================
  // EFFET - SURVEILLANCE ABONNEMENT
  // ===============================================
  useEffect(() => {
    const updateSubscriptionTier = () => {
      const tier = subscriptionService.getCurrentTier();
      setCurrentSubscriptionTier(tier);
    };

    const handleSubscriptionUpdate = (event: CustomEvent) => {
    
      updateSubscriptionTier();
    };

    updateSubscriptionTier();
    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate as EventListener);

    return () => {
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate as EventListener);
    };
  }, []);


  // Lecture XP + écoute des mises à jour
  useEffect(() => {
    if (!languageCode) return;
    const read = () => setXp(parseInt(localStorage.getItem(`${languageCode}-totalXP`) || '0'));
    read();
    window.addEventListener('xpUpdated', read);
    return () => window.removeEventListener('xpUpdated', read);
  }, [languageCode]);

  const readStreak = () => {
    try { return JSON.parse(localStorage.getItem('jogolinga-daily-streak') || '{}').streak ?? 0; }
    catch { return 0; }
  };

  const [streak, setStreak] = useState<number>(readStreak);

  useEffect(() => {
    const refresh = () => setStreak(readStreak());
    window.addEventListener('streakUpdated', refresh);
    return () => window.removeEventListener('streakUpdated', refresh);
  }, []);

  // Countdown "streak à risque" : affiché après 18h si l'utilisateur n'a pas encore pratiqué aujourd'hui
  const [streakCountdown, setStreakCountdown] = useState<string | null>(null);
  useEffect(() => {
    const compute = () => {
      const now = new Date();
      if (now.getHours() < 18) { setStreakCountdown(null); return; }
      const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      try {
        const history: string[] = JSON.parse(localStorage.getItem('jogolinga-activity-history') || '[]');
        if (history.includes(todayIso)) { setStreakCountdown(null); return; }
      } catch { setStreakCountdown(null); return; }
      const midnight = new Date(now); midnight.setHours(24, 0, 0, 0);
      const diffMs = midnight.getTime() - now.getTime();
      const h = Math.floor(diffMs / 3600000);
      const m = Math.floor((diffMs % 3600000) / 60000);
      setStreakCountdown(h > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${m}min`);
    };
    compute();
    const id = setInterval(compute, 30000);
    window.addEventListener('streakUpdated', compute);
    return () => {
      clearInterval(id);
      window.removeEventListener('streakUpdated', compute);
    };
  }, []);

  const levelInfo = languageCode ? getLevel(xp, languageCode) : null;

  // Fermer le dropdown langue au clic extérieur
  useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler as EventListener, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler as EventListener);
    };
  }, [langOpen]);

  // ===============================================
  // RENDU
  // ===============================================
  return (
    <header 
      ref={headerRef}
      className={`mobile-header ${isDarkMode ? 'dark' : 'light'} ${isMainMenu ? 'main-menu' : 'component-view'} ${isHeaderVisible ? 'visible' : 'hidden'}`}
    >
      {/* ===============================================
          SECTION GAUCHE
          =============================================== */}
      <div className="header-left">
        {/* Logo JogoLinga (menu principal uniquement) */}
        {isMainMenu && (
          <div className="header-logo-circle"><img src="/assets/logo-mark.svg" alt="JogoLinga" /></div>
        )}

        {/* Bouton retour */}
        {showBackButton && onBack && (
          <motion.button
            className="back-button-mobile"
            onClick={() => {
              if (currentMode === 'learn' && hasSelectedCategory) {
                onShowExitConfirmation?.();
              } else {
                onBack();
              }
            }}
            variants={buttonVariants}
            initial="initial"
            whileHover="hover"
            whileTap="tap"
            aria-label={t('header.back') as string}
          >
            <ArrowLeft size={20} />
          </motion.button>
        )}

        {/* Sélecteur de langue (menu principal uniquement) */}
        {shouldShowLanguageSelector && (() => {
          const LANGS = [
            { code: 'wf', flag: '🇸🇳', name: 'Wolof' },
            { code: 'ba', flag: '🇲🇱', name: 'Bambara' },
            { code: 'la', flag: '🇨🇩', name: 'Lingala' },
            { code: 'ff', flag: '🇬🇳', name: 'Peul' },
            { code: 'co', flag: '🇪🇬', name: 'Copte' },
            { code: 'sw', flag: '🇹🇿', name: 'Swahili' },
            { code: 'ka', flag: '𓋹', name: 'Kama' },
            { code: 'yo', flag: '🇳🇬', name: 'Yoruba' },
          ];
          const selected = LANGS.find(l => l.code === languageCode) ?? LANGS[0];
          return (
            <div ref={langDropdownRef} style={{ position: 'relative', flexShrink: 0 }}>
              {/* Trigger */}
              <motion.button
                onClick={() => setLangOpen(o => !o)}
                className="lang-selector-pill"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                style={{
                  background: isDarkMode ? 'rgba(0,0,0,0.28)' : '#F2E6CB',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.35)' : '2px solid var(--gold-primary)',
                  color: isDarkMode ? '#fff' : '#4A1E00',
                }}
              >
                <span style={{ fontSize: '16px', lineHeight: 1 }}><EmojiText text={selected.flag} /></span>
                <span>{selected.name}</span>
                {streak > 0 && (
                  <>
                    <span className="lang-pill-sep" />
                    <span
                      style={{ fontSize: '12px', fontWeight: 700, cursor: onStreakClick ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '3px' }}
                      onClick={onStreakClick ? e => { e.stopPropagation(); onStreakClick(); } : undefined}
                    >
                      <Flame size={13} style={{ flexShrink: 0 }} fill="currentColor" />{streak}
                      {streakCountdown && (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          background: isDarkMode ? 'rgba(239,68,68,0.18)' : 'rgba(242,230,203,0.25)',
                          color: isDarkMode ? 'var(--color-error)' : '#F2E6CB',
                          padding: '1px 5px',
                          borderRadius: '8px',
                          marginLeft: '2px',
                          animation: 'pulse 2s infinite',
                        }}>
                          {streakCountdown}
                        </span>
                      )}
                    </span>
                  </>
                )}
                <ChevronDown
                  size={13}
                  style={{
                    transition: 'transform 0.2s',
                    transform: langOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    opacity: 0.8,
                  }}
                />
              </motion.button>

              {/* Dropdown panel */}
              {langOpen && (
                <motion.div
                  data-lang-dropdown
                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  role="listbox"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    minWidth: '170px',
                    background: isDarkMode ? '#1e1a16' : '#EDD9A3',
                    border: `2px solid ${isDarkMode ? 'rgba(200,112,64,0.3)' : 'rgba(200,112,64,0.6)'}`,
                    borderRadius: '14px',
                    boxShadow: isDarkMode
                      ? '0 8px 24px rgba(0,0,0,0.55), 0 0 0 1px rgba(218,165,32,0.1)'
                      : '0 8px 24px rgba(200,112,64,0.25), 0 2px 8px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    zIndex: 200000,
                  }}
                >
                  {/* Section niveau */}
                  {levelInfo && (
                    <div style={{
                      padding: '12px 14px 10px',
                      borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(200,112,64,0.25)'}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        {user?.picture ? (
                          <img
                            src={user.picture}
                            alt={t('profile.identity.avatarAlt') as string}
                            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          />
                        ) : (
                          <div style={{
                            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                            background: 'linear-gradient(135deg, var(--gold-primary), var(--gold-light))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '13px', fontWeight: 700, color: '#fff',
                          }}>
                            {(user?.name ?? 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', opacity: 0.55, color: isDarkMode ? '#e8d5b0' : 'var(--gold-primary)' }}>{t('header.level', { level: levelInfo.current.level })}</div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: isDarkMode ? '#e8d5b0' : '#4A1E00' }}>{levelInfo.current.name}</div>
                        </div>
                      </div>
                      <div style={{ height: 5, background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(200,112,64,0.2)', borderRadius: 99, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${levelInfo.progress}%`, background: 'linear-gradient(90deg, var(--gold-primary), var(--gold-light))', borderRadius: 99, transition: 'width 0.4s ease' }} />
                      </div>
                      <div style={{ marginTop: '5px', fontSize: '11px', fontWeight: 600, color: isDarkMode ? 'var(--gold-primary)' : 'var(--gold-primary)', opacity: 0.85 }}>
                        {levelInfo.next ? t('header.xpToNext', { xp: levelInfo.next.min - xp, name: levelInfo.next.name }) : <EmojiText text={t('header.maxLevel') as string} />}
                      </div>
                    </div>
                  )}
                  {LANGS.map((lang, idx) => {
                    const isActive = lang.code === languageCode;
                    return (
                      <button
                        key={lang.code}
                        role="option"
                        aria-selected={isActive}
                        onClick={() => { onLanguageChange!(lang.code as LanguageCode); setLangOpen(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          width: '100%', padding: '11px 14px',
                          background: isActive
                            ? (isDarkMode ? 'rgba(218,165,32,0.18)' : 'rgba(200,112,64,0.22)')
                            : 'transparent',
                          borderLeft: isActive ? '3px solid var(--gold-primary)' : '3px solid transparent',
                          borderTop: 'none', borderRight: 'none',
                          borderBottom: isDarkMode
                            ? '1px solid rgba(255,255,255,0.06)'
                            : '1px solid rgba(200,112,64,0.2)',
                          cursor: 'pointer',
                          color: isDarkMode ? '#e8d5b0' : 'var(--gold-primary)',
                          fontSize: '14px',
                          fontWeight: isActive ? 700 : 600,
                          textAlign: 'left',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => {
                          if (!isActive) (e.currentTarget as HTMLButtonElement).style.background =
                            isDarkMode ? 'rgba(218,165,32,0.08)' : 'rgba(200,112,64,0.12)';
                        }}
                        onMouseLeave={e => {
                          if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                        }}
                      >
                        <span style={{ fontSize: '18px', lineHeight: 1 }}><EmojiText text={lang.flag} /></span>
                        <span style={{ flex: 1 }}>{lang.name}</span>
                        {isActive && <Check size={14} style={{ color: isDarkMode ? 'var(--gold-light)' : 'var(--gold-primary)', flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </div>
          );
        })()}

      </div>


      {/* ===============================================
          TITRE CENTRAL (composants uniquement)
          =============================================== */}
      {shouldShowTitle && (
        <div className="header-title">
          <h1>{title}</h1>
        </div>
      )}
      
      {/* ===============================================
          SECTION DROITE - ACTIONS
          =============================================== */}
      <div className="header-actions">
        {/* GoogleAuth */}
        <div className="mobile-google-auth-container">
          <GoogleAuth
            onLogin={(user) => {
              onLogin(user);
            }}
            onLogout={onLogout}
            isHeader={true}
          />
        </div>
        
        {/* Bouton toggle thème */}
        <motion.button 
          onClick={toggleTheme}
          className="theme-toggle-button"
          variants={buttonVariants}
          initial="initial"
          whileHover="hover"
          whileTap="tap"
          aria-label={t('header.toggleTheme') as string}
        >
          {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
        </motion.button>
      </div>
    </header>
  );
};

export default MobileHeader;
